import { StorageManager, GameRecord } from './storageManager';
import { calculatePoints } from './scoring';

/**
 * Score Correction Utility
 * 
 * This utility exists to retroactively fix user scores when we discover that
 * a past daily challenge had incorrect "actual year" data for one or more rounds.
 * 
 * Background: Sometimes we realize after a challenge goes live that we made an error 
 * in the year metadata for certain images. Rather than leave players with incorrect 
 * scores forever, this tool lets us push a correction that recalculates their scores
 * using the correct years.
 * 
 * The fix updates:
 * - Individual round scores in the detailed results data
 * - Total challenge score and average years off
 * - The game history record
 * - Recalculated all-time user statistics
 * 
 * This ensures that when we fix data issues, users don't lose their progress or
 * get stuck with unfair scores due to our mistakes.
 */

interface CorrectionInfo {
  roundIndex: number;
  correctYear: number;
}

/**
 * Applies score corrections for a specific challenge date.
 * 
 * @param challengeDate - The date string (YYYY-MM-DD) of the challenge to fix
 * @param corrections - Array of round corrections with the correct year data
 * @returns {boolean} Returns true if an update was made, false otherwise.
 */
export const fixChallengeScore = (
  challengeDate: string,
  corrections: CorrectionInfo[]
): boolean => {
  try {
    const resultsData = StorageManager.loadResultsData(challengeDate);
    if (!resultsData || !resultsData.roundData) {
      return false;
    }

    let scoreNeedsUpdate = false;
    let newTotalScore = 0;
    let totalYearsOff = 0;

    // Create a map of corrections for easy lookup
    const correctionMap = new Map(
      corrections.map((c) => [c.roundIndex, c.correctYear])
    );

    resultsData.roundData.forEach((round: any) => {
      const roundIndex = round.roundNumber - 1;
      const newCorrectYear = correctionMap.get(roundIndex);

      if (newCorrectYear !== undefined) {
        // This is a round that needs correction
        const newPoints = calculatePoints(round.guessedYear, newCorrectYear);
        if (round.score !== newPoints) {
          scoreNeedsUpdate = true;
          round.score = newPoints;
        }
        // Update the actualYear in the stored data as well
        round.actualYear = newCorrectYear;
      }
      newTotalScore += round.score;
      totalYearsOff += Math.abs(round.guessedYear - round.actualYear);
    });

    if (scoreNeedsUpdate) {
      // Recalculate average years off
      const newAverageYearsOff = totalYearsOff / resultsData.roundData.length;

      // Update the main results object
      resultsData.score = newTotalScore;
      resultsData.averageYearsOff = newAverageYearsOff;

      // Save the corrected results data
      StorageManager.saveResultsData(challengeDate, resultsData);

      // Update the game history array
      const gameHistory = StorageManager.getGameHistory();
      const historyIndex = gameHistory.findIndex(
        (game: GameRecord) => game.date === challengeDate
      );

      if (historyIndex > -1) {
        gameHistory[historyIndex].score = newTotalScore;
        gameHistory[historyIndex].averageYearsOff = newAverageYearsOff;
        localStorage.setItem(
          StorageManager.KEYS.GAME_HISTORY,
          JSON.stringify(gameHistory)
        );
      }

      // Finally, recalculate and save the all-time user stats
      // This ensures that global statistics like average score remain accurate
      const updatedStats =
        StorageManager._calculateStatsFromHistory(gameHistory);
      localStorage.setItem(
        StorageManager.KEYS.USER_STATS,
        JSON.stringify(updatedStats)
      );

      console.log(
        `[ScoreFix] Corrected score for ${challengeDate}. New Score: ${newTotalScore}`
      );
      return true;
    }
  } catch (error) {
    console.error(
      `[ScoreFix] Error fixing challenge score for ${challengeDate}:`,
      error
    );
  }
  return false;
};
